import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import {
  SG_FETCH_PRODUCT_PRICE,
  SG_EDIT_PRODUCT_PRICE,
  SG_ADD_PRODUCT_PRICE,
  SG_DELETE_PRODUCT_PRICE,
} from "constants/actions";
import {
  Icon,
  Message,
  Button,
  Header,
  Input,
  Checkbox,
  Dropdown,
  Confirm,
} from "semantic-ui-react";
import { BasicErrorMessage } from "utilities/ErrorMessages";

const PRICE_TYPE_OPTIONS = [
  { key: "recurring", text: "Recurring", value: "recurring" },
  { key: "one_time", text: "One Time", value: "one_time" },
];

const INTERVAL_OPTIONS = [
  { key: "none", text: "None", value: "none" },
  { key: "month", text: "Monthly", value: "month" },
  { key: "year", text: "Yearly", value: "year" },
];

const USAGE_TYPE_OPTIONS = [
  { key: "fixed", text: "Fixed Per Unit", value: "fixed" },
  { key: "licensed", text: "Licensed Per Seat", value: "licensed" },
];

const Details = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [templateId, setTemplateId] = useState(false);
  const [templateDetails, setTemplateDetails] = useState({
    amount: 0,
    currency: "CAD",
    frequency: "recurring",
    interval: "month",
    usage_type: "fixed",
    active: true,
  });
  const [formErrors, setFormErrors] = useState([]);
  const [pristine, setPristine] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setTemplateId(id);
  }, [id]);

  useEffect(() => {
    if (templateId) {
      dispatch({
        type: SG_FETCH_PRODUCT_PRICE,
        payload: { id: templateId },
      });
    }
  }, [dispatch, templateId]);

  const { get_product_price } = useSelector(
    (state) => ({
      get_product_price: state.product_price,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (get_product_price?.[templateId]) {
      setTemplateDetails(get_product_price?.[templateId]);
    }
  }, [get_product_price, templateId]);

  useEffect(() => {
    if (get_product_price?.updated?.id) {
      navigate(`/app/product/price/details/${get_product_price.updated.id}`);
    }
  }, [get_product_price.updated, navigate]);

  useEffect(() => {
    const ErrorList = [];
    if (!templateDetails?.amount) {
      ErrorList.push("Enter an amount");
    }
    setFormErrors(ErrorList);
  }, [templateDetails]);

  const handleChange = (e, { name, value }) => {
    setTemplateDetails({ ...templateDetails, [name]: value });
  };

  const handleSubmit = () => {
    setPristine(false);
    if (formErrors.length === 0) {
      const payload = {
        ...templateDetails,
      };
      if (templateId) {
        dispatch({
          type: SG_EDIT_PRODUCT_PRICE,
          payload: { id: templateId, ...payload },
        });
      } else {
        dispatch({
          type: SG_ADD_PRODUCT_PRICE,
          payload,
        });
      }
    }
  };

  const handleDelete = () => {
    if (templateId) {
      dispatch({
        type: SG_DELETE_PRODUCT_PRICE,
        payload: { id: templateId },
      });
    }
  };

  return (
    <>
      <Header as="h2">
        <Icon
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/app/product`)}
          name="arrow left"
        />
        {templateId
          ? `Edit Price of ${templateDetails?.amount}`
          : "Add New Product Price"}
      </Header>

      <BasicErrorMessage />
      {formErrors.length > 0 && !pristine && (
        <Message negative header="We need these details!" list={formErrors} />
      )}

      <FormColumn>
      <FormSection>
          <LabelHead>Name</LabelHead>
          <StyledInput
            name="name"
            value={templateDetails?.name}
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Amount</LabelHead>
          <StyledInput
            name="amount"
            type="number"
            value={templateDetails?.amount}
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Currency</LabelHead>
          <StyledInput
            name="currency"
            value={templateDetails?.currency}
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Frequency</LabelHead>
          <Dropdown
            placeholder="Select Frequency"
            selection
            options={PRICE_TYPE_OPTIONS}
            value={templateDetails?.frequency}
            name="frequency"
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Interval</LabelHead>
          <Dropdown
            placeholder="Select Interval"
            selection
            options={INTERVAL_OPTIONS}
            value={templateDetails?.interval}
            name="interval"
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Usage Type</LabelHead>
          <Dropdown
            placeholder="Select Usage Type"
            selection
            options={USAGE_TYPE_OPTIONS}
            value={templateDetails?.usage_type}
            name="usage_type"
            onChange={handleChange}
          />
        </FormSection>

        <FormSection>
          <LabelHead>Active</LabelHead>
          <StyledCheckbox
            name="active"
            checked={templateDetails?.active}
            onChange={(e, { name, checked }) =>
              setTemplateDetails({ ...templateDetails, [name]: checked })
            }
          />
        </FormSection>
      </FormColumn>

      <SectionDiv lastSection={true}>
        {templateId && (
          <>
            <Button
              floated="left"
              color="red"
              onClick={() => setConfirmOpen(true)}
            >
              Delete Price
            </Button>
            <Confirm
              open={confirmOpen}
              onCancel={() => setConfirmOpen(false)}
              onConfirm={handleDelete}
            />
          </>
        )}

        <Button floated="right" color="green" onClick={handleSubmit}>
          {templateId ? "Update Price" : "Create Price"}
        </Button>
      </SectionDiv>
    </>
  );
};

export default Details;

// Styled Components
const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0 20px; /* Add some padding for spacing */
`;

const FormSection = styled.div`
  margin-bottom: 15px;
  width: 100%; /* Full width */
`;

const LabelHead = styled.label`
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
`;

const StyledInput = styled(Input)`
  width: 100%; /* Ensure the input takes the full width */
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: 0; /* Adjust as needed */
`;

const SectionDiv = styled.div`
  padding: 10px 0;
  margin: 10px 0;
  margin-bottom: ${(props) => (props.lastSection === true ? "100px" : "20px")};
`;
